import { useState, useCallback } from 'react';
import Keycloak from 'keycloak-js';
import { 
  ApiResponse, 
  WebSocketConnection, 
  WebSocketConnectionsResponse, 
  BroadcastResponse 
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' 
    ? 'https://localhost:7292'
    : 'https://api.dotnethub.pl'
  );

interface UseApiReturn {
  apiResponse: ApiResponse | null;
  apiLoading: boolean;
  wsConnections: WebSocketConnection[];
  wsLoading: boolean;
  callApi: () => Promise<void>;
  fetchConnections: () => Promise<void>;
  sendBroadcast: (message: string) => Promise<boolean>;
}

export const useApi = (keycloak: Keycloak | null): UseApiReturn => {
  const [apiResponse, setApiResponse] = useState<ApiResponse | null>(null);
  const [apiLoading, setApiLoading] = useState<boolean>(false);
  const [wsConnections, setWsConnections] = useState<WebSocketConnection[]>([]);
  const [wsLoading, setWsLoading] = useState<boolean>(false);

  const handleMaintenanceMode = (response: Response) => {
    if (response.status === 503) {
      // Trigger maintenance mode - will be caught by useMaintenanceMode
      window.location.reload();
      return true;
    }
    return false;
  };

  const callApi = useCallback(async () => {
    if (!keycloak?.token) return;

    setApiLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/websockets/connections`, {
        headers: {
          'Authorization': `Bearer ${keycloak.token}`
        },
        credentials: 'include'
      });

      if (handleMaintenanceMode(response)) return;

      const data: WebSocketConnectionsResponse = await response.json();
      
      const userName = keycloak.tokenParsed?.preferred_username || 'Unknown';
      
      setApiResponse({
        message: 'API is working!',
        user: userName,
        authenticated: true,
        webSocketConnections: data.totalConnections
      });
    } catch (error) {
      console.error('API call failed:', error);
      setApiResponse({ 
        message: 'Error', 
        user: 'Unknown', 
        authenticated: false, 
        webSocketConnections: 0 
      });
    } finally {
      setApiLoading(false);
    }
  }, [keycloak]);

  const fetchConnections = useCallback(async () => {
    if (!keycloak?.token) return;

    setWsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/websockets/connections`, {
        headers: {
          'Authorization': `Bearer ${keycloak.token}`
        },
        credentials: 'include'
      });

      if (handleMaintenanceMode(response)) return;

      const data: WebSocketConnectionsResponse = await response.json();
      setWsConnections(data.connections || []);
    } catch (error) {
      console.error('Failed to fetch connections:', error);
      setWsConnections([]);
    } finally {
      setWsLoading(false);
    }
  }, [keycloak]);

  const sendBroadcast = useCallback(async (message: string): Promise<boolean> => {
    if (!keycloak?.token || !message.trim()) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/websockets/broadcast`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${keycloak.token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ message })
      });

      if (handleMaintenanceMode(response)) return false;
      
      const result: BroadcastResponse = await response.json();
      
      if (result.success) {
        alert(`Message sent to ${result.successCount} connections!`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Broadcast failed:', error);
      alert('Failed to send broadcast');
      return false;
    }
  }, [keycloak]);

  return {
    apiResponse,
    apiLoading,
    wsConnections,
    wsLoading,
    callApi,
    fetchConnections,
    sendBroadcast
  };
};
